import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from './ui/select';
import { useFeature } from '../hooks/useFeatures';
import {
  Loader2, Sparkles, Wand2, Plus, X, RefreshCw, Save, Download, ImageIcon,
} from 'lucide-react';
import { toast } from 'sonner';

const STYLE_PRESETS = [
  { id: 'bold', label: 'Bold marketing poster', prompt: 'bold, high-impact marketing poster style with strong typography' },
  { id: 'minimal', label: 'Minimal / clean', prompt: 'minimal, clean, lots of white space, refined modern aesthetic' },
  { id: 'mockup', label: 'Device mockup showcase', prompt: 'product showcase featuring a sleek laptop/phone device mockup displaying the product UI' },
  { id: 'vibrant', label: 'Gradient / vibrant', prompt: 'vibrant gradients, energetic and colorful, eye-catching' },
  { id: 'editorial', label: 'Editorial / magazine', prompt: 'editorial magazine layout, premium typographic hierarchy' },
];

const SIZES = [
  { id: '1024x1536', label: 'Portrait 2:3 (1024×1536)' },
  { id: '1024x1024', label: 'Square 1:1 (1024×1024)' },
  { id: '1536x1024', label: 'Landscape 3:2 (1536×1024)' },
];

/**
 * AIPosterStudio — generates a polished, text-rich marketing graphic with
 * GPT Image 1 (OpenAI's latest, strong at rendering legible text + layout).
 * Structured inputs are composed into a single design prompt; the backend
 * skips the "realistic photo" wrapper via style:"design".
 */
export const AIPosterStudio = ({ kits = [] }) => {
  const { api } = useAuth();
  const navigate = useNavigate();

  const [kitId, setKitId] = useState(kits[0]?.id || '');
  const [headline, setHeadline] = useState('');
  const [subhead, setSubhead] = useState('');
  const [bullets, setBullets] = useState(['']);
  const [style, setStyle] = useState('bold');
  const [details, setDetails] = useState('');
  const [size, setSize] = useState('1024x1536');
  const [hq, setHq] = useState(false);
  const hdFeature = useFeature('poster_hd_quality');

  const [generating, setGenerating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [image, setImage] = useState(null); // base64 (no prefix)

  const kit = kits.find((k) => k.id === kitId) || null;
  const dataUrl = image ? `data:image/png;base64,${image}` : null;

  const setBullet = (i, v) => setBullets((b) => b.map((x, idx) => (idx === i ? v : x)));
  const addBullet = () => setBullets((b) => (b.length >= 5 ? b : [...b, '']));
  const removeBullet = (i) => setBullets((b) => b.filter((_, idx) => idx !== i));

  const buildPrompt = () => {
    const bl = bullets.map((b) => b.trim()).filter(Boolean);
    const styleText = STYLE_PRESETS.find((s) => s.id === style)?.prompt || '';
    const brand = kit
      ? `Brand name "${kit.name}". Use the brand palette: ${kit.primary_color || '#4f46e5'} as primary and ${kit.accent_color || '#f59e0b'} as accent. Place a small clean logo/brand mark for "${kit.name}".`
      : '';
    return [
      `A polished, professional social media marketing graphic in a ${styleText}.`,
      headline ? `Large bold headline reading exactly: "${headline}".` : '',
      subhead ? `A supporting subheadline reading: "${subhead}".` : '',
      bl.length ? `Show these feature points as a clean vertical list, each with a small matching icon: ${bl.map((b) => `"${b}"`).join('; ')}.` : '',
      brand,
      details ? `Additional creative direction: ${details}.` : '',
      'Modern sans-serif typography, strong visual hierarchy, generous spacing, high-end agency look. Keep all text perfectly legible and correctly spelled.',
    ].filter(Boolean).join(' ');
  };

  const generate = async () => {
    if (!headline.trim() && !details.trim()) {
      toast.error('Add a headline or some details to describe the image');
      return;
    }
    setGenerating(true);
    try {
      const r = await api.post('/generate/image', {
        prompt: buildPrompt(),
        model: 'gpt-image-1',
        quality: (hq && !hdFeature.locked) ? 'high' : 'medium',
        size,
        style: 'design',
      });
      if (r.data?.image_base64) {
        setImage(r.data.image_base64);
        toast.success('Poster generated');
      } else {
        toast.error('No image returned — try again');
      }
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : (d?.message || 'Generation failed'));
    } finally {
      setGenerating(false);
    }
  };

  const download = () => {
    if (!dataUrl) return;
    const a = document.createElement('a');
    a.href = dataUrl; a.download = `poster-${Date.now()}.png`;
    document.body.appendChild(a); a.click(); a.remove();
  };

  const saveAsDraft = async () => {
    if (!dataUrl) return;
    setSaving(true);
    try {
      const blob = await (await fetch(dataUrl)).blob();
      const file = new File([blob], `poster-${Date.now()}.png`, { type: 'image/png' });
      const fd = new FormData();
      fd.append('file', file);
      const up = await api.post('/uploads/media', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      await api.post('/posts', {
        caption: [headline, subhead].filter(Boolean).join('\n') || 'AI Poster',
        platforms: ['Instagram'],
        post_title: headline || 'AI Poster',
        media_items: [up.data],
      });
      toast.success('Saved as draft in My Posts');
      navigate('/posts');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not save poster');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-testid="ai-poster-studio">
      {/* Inputs */}
      <div className="space-y-4">
        <Card><CardContent className="p-4 space-y-4">
          <div className="space-y-1">
            <Label className="text-xs">Headline</Label>
            <Input value={headline} onChange={(e) => setHeadline(e.target.value)} placeholder="e.g. Create more content — faster." data-testid="poster-headline" />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Subheadline (optional)</Label>
            <Input value={subhead} onChange={(e) => setSubhead(e.target.value)} placeholder="e.g. AI tools that help you ideate, design, and write in minutes" data-testid="poster-subhead" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Feature points (up to 5)</Label>
            {bullets.map((b, i) => (
              <div key={i} className="flex items-center gap-2">
                <Input value={b} onChange={(e) => setBullet(i, e.target.value)} placeholder={`Point ${i + 1} — e.g. Plan campaigns in seconds`} data-testid={`poster-bullet-${i}`} />
                {bullets.length > 1 && (
                  <button onClick={() => removeBullet(i)} className="text-zinc-400 hover:text-rose-500" data-testid={`poster-bullet-remove-${i}`}><X className="w-4 h-4" /></button>
                )}
              </div>
            ))}
            {bullets.length < 5 && (
              <button onClick={addBullet} className="text-xs text-indigo-600 hover:underline flex items-center gap-1" data-testid="poster-add-bullet">
                <Plus className="w-3.5 h-3.5" /> Add point
              </button>
            )}
          </div>
        </CardContent></Card>

        <Card><CardContent className="p-4 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label className="text-xs">Style</Label>
              <Select value={style} onValueChange={setStyle}>
                <SelectTrigger data-testid="poster-style"><SelectValue /></SelectTrigger>
                <SelectContent>{STYLE_PRESETS.map((s) => <SelectItem key={s.id} value={s.id}>{s.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Size</Label>
              <Select value={size} onValueChange={setSize}>
                <SelectTrigger data-testid="poster-size"><SelectValue /></SelectTrigger>
                <SelectContent>{SIZES.map((s) => <SelectItem key={s.id} value={s.id}>{s.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1 col-span-2">
              <Label className="text-xs">Brand kit (colors + name)</Label>
              <Select value={kitId || 'none'} onValueChange={(v) => setKitId(v === 'none' ? '' : v)}>
                <SelectTrigger data-testid="poster-brandkit"><SelectValue placeholder="None" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No brand kit</SelectItem>
                  {kits.map((k) => <SelectItem key={k.id} value={k.id}>{k.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Describe the image (extra details, optional)</Label>
            <Textarea rows={3} value={details} onChange={(e) => setDetails(e.target.value)} maxLength={1500}
              placeholder="e.g. Show a laptop mockup displaying the app dashboard, a numbered footer band, a lightbulb icon, indigo + white color scheme" data-testid="poster-details" />
          </div>
          <label className={`flex items-center gap-2 text-[11px] ${hdFeature.locked ? 'text-zinc-400' : 'text-zinc-600 cursor-pointer'}`} data-testid="poster-hq-toggle">
            <input type="checkbox" checked={hq && !hdFeature.locked} disabled={hdFeature.locked}
              onChange={(e) => setHq(e.target.checked)} className="accent-indigo-600" />
            HD quality (sharper, costs more credits)
            {hdFeature.locked && <span className="text-[10px] text-amber-600">— top tier only</span>}
          </label>
          <Button onClick={generate} disabled={generating} className="w-full bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="poster-generate-btn">
            {generating ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Wand2 className="w-4 h-4 mr-2" />}
            {generating ? 'Generating (GPT Image 1)…' : 'Generate poster'}
          </Button>
          <p className="text-[11px] text-zinc-400">Uses GPT Image 1. Each poster consumes several image credits (more than a standard Nano Banana image). Text isn't always perfect — regenerate if a word renders off.</p>
        </CardContent></Card>
      </div>

      {/* Preview */}
      <div>
        <Card><CardContent className="p-4">
          {generating ? (
            <div className="flex flex-col items-center justify-center py-24 text-center gap-3" data-testid="poster-generating">
              <Loader2 className="w-7 h-7 animate-spin text-indigo-600" />
              <p className="text-sm text-zinc-600">Designing your poster… (~10–20s)</p>
            </div>
          ) : dataUrl ? (
            <div className="space-y-3" data-testid="poster-result">
              <img src={dataUrl} alt="AI poster" className="w-full rounded-lg border border-zinc-200" />
              <div className="flex flex-wrap gap-2">
                <Button onClick={generate} variant="outline" size="sm" data-testid="poster-regenerate-btn"><RefreshCw className="w-4 h-4 mr-1.5" /> Regenerate</Button>
                <Button onClick={download} variant="outline" size="sm" data-testid="poster-download-btn"><Download className="w-4 h-4 mr-1.5" /> Download</Button>
                <Button onClick={saveAsDraft} disabled={saving} size="sm" className="bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="poster-save-btn">
                  {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <Save className="w-4 h-4 mr-1.5" />} Save as draft
                </Button>
              </div>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-24 text-center gap-3 text-zinc-400" data-testid="poster-empty">
              <ImageIcon className="w-8 h-8 text-indigo-200" />
              <p className="text-sm">Your AI poster will appear here.</p>
              <p className="text-[11px] flex items-center gap-1"><Sparkles className="w-3 h-3" /> Fill in a headline + details, then Generate.</p>
            </div>
          )}
        </CardContent></Card>
      </div>
    </div>
  );
};

export default AIPosterStudio;
